import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { Breadcrumb, Button, message, Modal, Spin } from "antd";
import {
  ArrowLeftOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  MenuUnfoldOutlined,
  SaveOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "./sidebar/Sidebar";
import Overview from "./overview";
import Content, { ContentRef, ScoringFormValues } from "./content";
import {
  deleteDraft,
  markSubmit,
  resultOfTeacher,
  saveDraft,
} from "../../../service/scoring";
import { routesConfig } from "../../../config/routes";
import {
  GroupsExam,
  Draft,
  QuizProps,
  UserInfoProps,
  ScoreConfigType,
} from "../../../types/scoring-course";
import "./style.scss";
import { addOrUpdateQuizMarkRequest } from "../../../utils/arrays";
import { isEmpty, isNumber, toNumber, toString } from "lodash";
import { useViewport } from "../../../hooks/useViewport";
import CustomDraw from "../../../components/drawer/CustomDraw";

function ScoringDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const sessionId = params?.id;
  const contentRef = useRef<ContentRef>(null);
  const [scoreConfig, setScoreConfig] = useState<any>(null);

  // breadcrumb info
  const courseId = location.state?.courseId || scoreConfig?.courseId;
  const courseName = location.state?.courseName || scoreConfig?.courseName;;
  const unitName = location.state?.unitName || scoreConfig?.name;;
  const blockId = location.state?.blockId || scoreConfig?.blockId;;
  const studentName = location?.state?.studentName || t("Không có tên", "Không có tên");
  const isEdit = location?.state?.edit;

  // data get from api
  const [groups, setGroups] = useState<GroupsExam[]>([]);
  const [quizzes, setQuizzes] = useState<QuizProps[]>([]);
  const [currentQuiz, setCurrentQuiz] = useState<QuizProps>();
  const [userInfo, setUserInfo] = useState<UserInfoProps>();

  // control
  const [loading, setLoading] = useState<boolean>(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [quizzesCompleted, setQuizzesCompleted] = useState<Draft[]>([]);
  const [commentOverview, setCommentOverview] = useState<string>("");
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [isAbleToMark, setIsAbleToMark] = useState<boolean>(true); // Cho phép gửi kết quả

  const isDisableAll = mode === "view";
  const isDisableSendBtn = !isAbleToMark;
  const isShowBtnRemoveDaft = quizzes?.some(
    (item: QuizProps) => item?.result?.draftInfo || item?.result?.isDraft
  );
  const { width } = useViewport();

  const points =
    quizzesCompleted?.reduce((total: number, item: Draft) => {
      return total + item.points;
    }, 0) || 0;

  const totalPoints =
    quizzes?.reduce((total: number, item: QuizProps) => {
      return total + item?.result?.totalPoints;
    }, 0) || 0;

  useEffect(() => {
    if (isEdit) setMode("edit");
  }, [isEdit]);

  useEffect(() => {
    const getResults = async () => {
      if (sessionId) {
        try {
          setLoading(true);
          const res = await resultOfTeacher(sessionId);
          setUserInfo(res.data?.data?.userInfo);
          setIsAbleToMark(res.data?.data?.isAbleToMark);
          setScoreConfig(res.data?.data?.unitConfig);
          const groupsExam: GroupsExam[] = res.data?.data?.groups;
          if (groupsExam) {
            setGroups(groupsExam);
            let quizzesFLat: QuizProps[] = [];
            for (let i = 0; i < groupsExam?.length; i++) {
              setQuizzes((prevState: QuizProps[]) => [
                ...prevState,
                ...groupsExam[i]?.quiz,
              ]);
              quizzesFLat = [...quizzesFLat, ...groupsExam[i]?.quiz];
            }

            const quizzesFLatCompleted = quizzesFLat.filter(
              (item: QuizProps) =>
                !isEmpty(item.result?.draftInfo) ||
                !isEmpty(item.result?.markedTeacherId) ||
                !isEmpty(item.result?.results)
              // && item?.result?.isTrue !== null
            );

            setQuizzesCompleted(
              quizzesFLatCompleted?.map((item: QuizProps) => ({
                quizId: item.id,
                comment:
                  item.result?.draftInfo?.comment || item.result?.comment || "",
                points: item.result?.draftInfo?.points || item.result?.points,
                isConfigPointForTeacher:
                  item.result?.markedTeacherId ||
                  !isEmpty(item.result?.draftInfo)
                    ? true
                    : false,
              }))
            );
          }
          setCommentOverview(res.data?.data?.result?.commentByTeacher);
        } catch (err) {
          console.log(err);
        } finally {
          setLoading(false);
        }
      }
    };
    getResults();

    return () => setQuizzes([]);
  }, [reload]);
  const breadcrumb = [
    {
      title: (
        <Link to={`${routesConfig.courseManagement}/${routesConfig.courseList}`}>{t("Danh sách khoá học", "Danh sách khoá học")}</Link>
      ),
    },
    {
      title: (
        <Link
          to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.gradingList}`}
          state={{
            name: courseName,
          }}
        >
          {courseName}
        </Link>
      ),
    },
    {
      title: (
        <Link
          to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.grading}/${blockId}/exercise`}
          state={{
            courseName: courseName,
            unitName: unitName,
          }}
        >
          {unitName}
        </Link>
      ),
    },
    {
      title: studentName,
    },
  ];

  const handleChangeValues = (values?: ScoringFormValues) => {
    console.log(values);
    if (isNumber(values?.points) && isNumber(currentQuiz?.id)) {
      setQuizzesCompleted((prevQuizzes: Draft[]) =>
        addOrUpdateQuizMarkRequest(prevQuizzes, {
          quizId: toNumber(currentQuiz?.id),
          comment: toString(values?.comment),
          points: toNumber(values?.points),
          isConfigPointForTeacher: values?.isConfigPointForTeacher,
        })
      );
    }
  };

  const handleClickMenu = (quiz: any) => {
    setCurrentQuiz(quiz);
  };

  const handleSendResult = () => {
    Modal.confirm({
      type: "info",
      icon: <InfoCircleOutlined className="text-primary" />,
      centered: true,
      title: t("Xác nhận gửi chấm điểm", "Xác nhận gửi chấm điểm"),
      content: (
        <>
          <p className="text-14">
            {t("Đã chấm:", "Đã chấm:")} {quizzesCompleted?.length || 0}/{quizzes?.length || 0}
          </p>
          <p className="text-14">
            {t("Tổng điểm:", "Tổng điểm:")} {points}/{totalPoints}
          </p>
          <p className="text-orange text-14">
            Chấm điểm đã gửi sẽ không thể thu hồi. Vui lòng kiểm tra và cập nhập
            đầy đủ trước khi gửi
          </p>
        </>
      ),
      okText: t("Gửi chấm điểm", "Gửi chấm điểm"),
      cancelText: t("Huỷ", "Huỷ"),
      onOk: async () => {
        if (sessionId) {
          try {
            setLoadingSubmit(true);
            await markSubmit({
              sessionId: sessionId as string,
              commentByTeacher: commentOverview,
              data: quizzesCompleted?.map((quiz: Draft) => ({
                quizId: quiz.quizId,
                points: quiz.points,
                comment: quiz.comment,
              })),
            });
            setReload(!reload);
            message.success(t("Gửi chấm điểm thành công", "Gửi chấm điểm thành công"));
            setMode("view");
          } catch (err) {
            console.error(err);
          } finally {
            setLoadingSubmit(false);
          }
        }
      },
    });
  };

  const handleDeleteDaft = () => {
    Modal.confirm({
      type: "warning",
      icon: <InfoCircleOutlined style={{ color: "#FFC53D" }} />,
      centered: true,
      title: t("Xác nhận đặt lại kết quả chấm", "Xác nhận đặt lại kết quả chấm"),
      content:
        t("Toàn bộ thông tin đã xoá bao gồm điểm và nhận xét sẽ không thể khôi phục. Bạn có chắc chắn muốn Đặt lại kết quả chấm?", "Toàn bộ thông tin đã xoá bao gồm điểm và nhận xét sẽ không thể khôi phục. Bạn có chắc chắn muốn Đặt lại kết quả chấm?"),
      okText: t("Xác nhận", "Xác nhận"),
      cancelText: t("Huỷ", "Huỷ"),
      onOk: async () => {
        if (sessionId) {
          await deleteDraft(sessionId);
          setReload(!reload);
          message.success(t("Đặt lại kết quả thành công", "Đặt lại kết quả thành công"));
          setQuizzesCompleted([]);
          contentRef?.current?.resetFields();
        }
      },
    });
  };

  const handleSaveDaft = async () => {
    if (!isEmpty(quizzesCompleted)) {
      Modal.confirm({
        type: "info",
        icon: <InfoCircleOutlined className="text-primary" />,
        centered: true,
        title: t("Xác nhận lưu chấm điểm", "Xác nhận lưu chấm điểm"),
        content: (
          <>
            <p className="text-14">
              {t("Đã chấm:", "Đã chấm:")} {quizzesCompleted?.length || 0}/{quizzes?.length || 0}
            </p>
            <p className="text-14">
              {t("Tổng điểm:", "Tổng điểm:")} {points}/{totalPoints}
            </p>
            <p className="text-orange text-14">
              Đảm bảo tất cả các thông tin liên quan đã được kiểm tra và cập
              nhập đầy đủ trước khi lưu
            </p>
          </>
        ),
        okText: t("Lưu chấm điểm", "Lưu chấm điểm"),
        cancelText: t("Tiếp tục chấm", "Tiếp tục chấm"),
        onOk: async () => {
          if (sessionId) {
            try {
              setLoadingSubmit(true);
              await saveDraft({
                sessionId: sessionId as string,
                commentByTeacher: commentOverview,
                data: quizzesCompleted?.map((quiz: Draft) => ({
                  quizId: quiz.quizId,
                  points: quiz.points,
                  comment: quiz.comment,
                })),
              });
              setReload(!reload);
              message.success(t("Lưu chấm điểm thành công", "Lưu chấm điểm thành công"));
              setMode("view");
            } catch (err) {
              console.error(err);
            } finally {
              setLoadingSubmit(false);
            }
          }
        },
      });
    }
  };

  const renderButtonByMode = () => {
    switch (mode) {
      case "view":
        return (
          <Button
            className="btn-primary"
            icon={<SaveOutlined />}
            onClick={() => setMode("edit")}
          >
            {t("Chấm điểm", "Chấm điểm")}
          </Button>
        );
      case "edit":
        return (
          <Button
            className="btn-primary"
            icon={<SaveOutlined />}
            onClick={handleSaveDaft}
            loading={loadingSubmit}
          >
            {t("Lưu chấm điểm", "Lưu chấm điểm")}
          </Button>
        );
      default:
        break;
    }
  };

  return (
    <div className="scoring-detail-page">
      <div className="scoring-detail-page-header">
        <Breadcrumb items={breadcrumb} className="mb-1" />
        <div className="flex justify-between align-center gap-8">
          <div className="flex gap-8 align-center flex-1">
            <ArrowLeftOutlined
              className="arrowleft-icon"
              onClick={() => navigate(`${routesConfig.courseManagement}/${courseId}/${routesConfig.grading}/${blockId}/exercise`)}
            />
            <h3 className="page-title">{studentName}</h3>
          </div>
          {isShowBtnRemoveDaft && (
            <Button danger icon={<CloseOutlined />} onClick={handleDeleteDaft}>
              {t("Đặt lại kết quả chấm", "Đặt lại kết quả chấm")}
            </Button>
          )}
          {renderButtonByMode()}
          <Button
            disabled={isDisableSendBtn}
            icon={<SendOutlined />}
            onClick={handleSendResult}
          >
            {t("Gửi kết quả", "Gửi kết quả")}
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className="scoring-detail-content-page w-full">
          {width < 1000 ? (
            <CustomDraw
              width={500}
              placement="left"
              className="scoring-custom-draw"
              title={t("Thảo luận", "Thảo luận")}
              content={
                <Sidebar
                  groups={groups}
                  showCollapseBtn={false}
                  quizzes={quizzes}
                  onClick={handleClickMenu}
                />
              }
            >
              {" "}
              <div
                className="btn-collapsed pointer center"
                style={{ width: 50, height: 50 }}
              >
                <MenuUnfoldOutlined />
              </div>
            </CustomDraw>
          ) : (
            <Sidebar
              groups={groups}
              quizzes={quizzes}
              onClick={handleClickMenu}
            />
          )}

          <Content
            ref={contentRef}
            quizzesCompleted={quizzesCompleted}
            disabled={isDisableAll}
            currentQuiz={currentQuiz as QuizProps}
            onChange={handleChangeValues}
            commentOverview={commentOverview}
            onChangeOverviewComment={setCommentOverview}
          />

          {width < 1400 ? (
            <CustomDraw
              width={500}
              className="scoring-custom-draw"
              title={t("Thảo luận", "Thảo luận")}
              content={
                <Overview
                  groups={groups}
                  userInfo={userInfo}
                  showCollapseBtn={false}
                  quizzes={quizzes}
                  quizzesCompleted={quizzesCompleted}
                  scoreConfig={scoreConfig}
                  totalPoints={totalPoints}
                />
              }
            >
              {" "}
              <div
                className="btn-collapsed pointer center"
                style={{ width: 50, height: 50 }}
              >
                <MenuUnfoldOutlined />
              </div>
            </CustomDraw>
          ) : (
            <Overview
              groups={groups}
              userInfo={userInfo}
              quizzes={quizzes}
              quizzesCompleted={quizzesCompleted}
              scoreConfig={scoreConfig}
              totalPoints={totalPoints}
            />
          )}
        </div>
      </Spin>
    </div>
  );
}

export default ScoringDetail;
